import React, { useState, useEffect, useCallback } from 'react';
import {
  Banknote, Plus, ChevronRight, Check, X, Download, Eye,
  Calendar, Clock, Store, ArrowLeft, FileText
} from 'lucide-react';
import api from '../../api/client';
import { toast } from 'sonner';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid={`settlement-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
          <p className="font-heading text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
    </div>
  );
}

export default function SettlementsTab() {
  const { formatMoney, formatShortDate, currency: activeCurrency } = useCurrency();
  const [settlements, setSettlements] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/api/store/admin/settlements/?${params}`);
      setSettlements(res.data.settlements);
      setTotal(res.data.total);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!dateFrom || !dateTo) { toast.error('Select date range'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/api/store/admin/settlements/generate', { date_from: dateFrom, date_to: dateTo });
      toast.success(`Settlement batch generated — ${res.data.store_count} store(s)`);
      setShowGenerate(false);
      setDateFrom(''); setDateTo('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate');
    } finally { setGenerating(false); }
  };

  const handleAction = async (batchId, action, notes = '') => {
    setActionLoading(action);
    try {
      if (action === 'approve') {
        await api.put(`/api/store/admin/settlements/${batchId}/approve`, { notes });
        toast.success('Settlement approved');
      } else if (action === 'reject') {
        const reason = prompt('Rejection reason:');
        if (!reason) { setActionLoading(''); return; }
        await api.put(`/api/store/admin/settlements/${batchId}/reject`, { notes: reason });
        toast.success('Settlement rejected');
      } else if (action === 'mark-paid') {
        await api.put(`/api/store/admin/settlements/${batchId}/mark-paid`);
        toast.success('Settlement marked as paid');
      }
      // Refresh detail
      const res = await api.get(`/api/store/admin/settlements/${batchId}`);
      setSelectedBatch(res.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActionLoading(''); }
  };

  const handleExport = async (batchId) => {
    try {
      const res = await api.get(`/api/store/admin/settlements/${batchId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `settlement_${batchId.slice(0, 8)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const viewDetail = async (batch) => {
    try {
      const res = await api.get(`/api/store/admin/settlements/${batch.id}`);
      setSelectedBatch(res.data);
    } catch { setSelectedBatch(batch); }
  };

  // KPI calculations
  const pendingCount = settlements.filter(s => s.status === 'pending').length;
  const totalPaid = settlements.filter(s => s.status === 'paid').reduce((s, b) => s + b.total_net_payable, 0);
  const totalPending = settlements.filter(s => s.status === 'pending').reduce((s, b) => s + b.total_net_payable, 0);

  if (selectedBatch) {
    return (
      <div className="space-y-6" data-testid="settlement-detail">
        <button onClick={() => setSelectedBatch(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#561D1A] transition-colors" data-testid="back-to-settlements">
          <ArrowLeft size={16} /> Back to Settlements
        </button>

        {/* Batch Header */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-gray-800">Settlement Batch</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_COLORS[selectedBatch.status]}`}>
                  {selectedBatch.status}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Period: {selectedBatch.period_from} to {selectedBatch.period_to}
              </p>
              <p className="text-xs text-gray-400 mt-1">ID: {selectedBatch.id?.slice(0, 12)}...</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedBatch.status === 'pending' && (
                <>
                  <button onClick={() => handleAction(selectedBatch.id, 'approve')} disabled={!!actionLoading}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="approve-settlement">
                    <Check size={15} /> {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
                  </button>
                  <button onClick={() => handleAction(selectedBatch.id, 'reject')} disabled={!!actionLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="reject-settlement">
                    <X size={15} /> Reject
                  </button>
                </>
              )}
              {selectedBatch.status === 'approved' && (
                <button onClick={() => handleAction(selectedBatch.id, 'mark-paid')} disabled={!!actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="mark-paid-settlement">
                  <Banknote size={15} /> {actionLoading === 'mark-paid' ? 'Processing...' : 'Mark as Paid'}
                </button>
              )}
              <button onClick={() => handleExport(selectedBatch.id)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 flex items-center gap-2" data-testid="export-settlement">
                <Download size={15} /> Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase">Gross Amount</p>
            <p className="text-lg font-bold text-gray-800 mt-1">{formatAmount(selectedBatch.total_gross, activeCurrency)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase">Total Commission</p>
            <p className="text-lg font-bold text-[#EC1A23] mt-1">{formatAmount(selectedBatch.total_commission, activeCurrency)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase">Net Payable</p>
            <p className="text-lg font-bold text-emerald-600 mt-1">{formatAmount(selectedBatch.total_net_payable, activeCurrency)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-xs text-gray-500 uppercase">Stores / Transactions</p>
            <p className="text-lg font-bold text-gray-800 mt-1">{selectedBatch.store_count} / {selectedBatch.transaction_count}</p>
          </div>
        </div>

        {/* Store Breakdown Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">Store-by-Store Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="settlement-breakdown-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Store</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Gross</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Commission %</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Net Payable</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Txns</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Frequency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {selectedBatch.store_breakdowns?.map((b, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="px-6 py-3 font-medium text-gray-800">{b.store_name}</td>
                    <td className="px-6 py-3 text-right text-gray-600">{formatAmount(b.gross_amount, activeCurrency)}</td>
                    <td className="px-6 py-3 text-right text-gray-600">{b.commission_rate}%</td>
                    <td className="px-6 py-3 text-right text-[#EC1A23] font-medium">{formatAmount(b.commission_amount, activeCurrency)}</td>
                    <td className="px-6 py-3 text-right text-emerald-600 font-medium">{formatAmount(b.net_payable, activeCurrency)}</td>
                    <td className="px-6 py-3 text-right text-gray-600">{b.transaction_count}</td>
                    <td className="px-6 py-3 text-right">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 capitalize">{b.settlement_frequency}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes */}
        {selectedBatch.notes && (
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <p className="text-xs text-amber-600 font-semibold uppercase mb-1">Notes</p>
            <p className="text-sm text-amber-800">{selectedBatch.notes}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settlements-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total Batches" value={total} icon={FileText} color="bg-[#561D1A]" />
        <KPICard label="Pending Approval" value={pendingCount} icon={Clock} color="bg-amber-500" />
        <KPICard label="Pending Amount" value={formatAmount(totalPending, activeCurrency)} icon={Banknote} color="bg-blue-600" />
        <KPICard label="Total Paid" value={formatAmount(totalPaid, activeCurrency)} icon={Check} color="bg-emerald-600" />
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {['', 'pending', 'approved', 'paid', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${statusFilter === s ? 'bg-[#561D1A] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#561D1A]/30'}`}
              data-testid={`filter-${s || 'all'}`}>
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
        <button onClick={() => setShowGenerate(true)}
          className="px-5 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] flex items-center gap-2 transition-all hover:shadow-lg" data-testid="generate-settlement-btn">
          <Plus size={16} /> Generate Settlement
        </button>
      </div>

      {/* Generate Modal */}
      {showGenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowGenerate(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="generate-settlement-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Generate Settlement Batch</h3>
            <p className="text-sm text-gray-500 mb-4">Select a date range to calculate store commissions from redemptions.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 uppercase">From</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="settlement-date-from" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">To</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="settlement-date-to" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowGenerate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleGenerate} disabled={generating}
                className="px-5 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50" data-testid="confirm-generate-btn">
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settlements List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : settlements.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <Banknote size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-800 mb-2">No Settlements Yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">Generate your first settlement batch to calculate store commissions from redemptions.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="settlements-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Stores</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Gross</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Commission</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Net Payable</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Generated</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {settlements.map(s => (
                <tr key={s.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-800">{s.period_from} — {s.period_to}</p>
                    <p className="text-xs text-gray-400">{s.transaction_count} transactions</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_COLORS[s.status]}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-600">{s.store_count}</td>
                  <td className="px-6 py-4 text-right text-gray-600">{formatAmount(s.total_gross, activeCurrency)}</td>
                  <td className="px-6 py-4 text-right text-[#EC1A23]">{formatAmount(s.total_commission, activeCurrency)}</td>
                  <td className="px-6 py-4 text-right text-emerald-600 font-medium">{formatAmount(s.total_net_payable, activeCurrency)}</td>
                  <td className="px-6 py-4 text-right text-xs text-gray-400">{formatShortDate(s.generated_at)}</td>
                  <td className="px-6 py-4 text-center">
                    <button onClick={() => viewDetail(s)} className="text-[#561D1A] hover:text-[#EC1A23] transition-colors" data-testid={`view-settlement-${s.id?.slice(0,8)}`}>
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
